import * as React from "react";
import { observer } from "mobx-react";
import { useSearchParams } from "react-router-dom";
import { UserDataStoreContext } from "../store";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const defaultTheme = createTheme();

// Sign-in screen for the single sign-on flow. There are no credentials to collect:
// the button hands the browser to /api/auth/login, which redirects on to the identity
// provider. Reached explicitly after logout, or with ?error= when a callback failed.
const Login = observer(() => {
  const userDataStore = React.useContext(UserDataStoreContext);
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");

  // Brief settle before showing the button, so a redirect that is already under way
  // does not flash a sign-in prompt.
  const [ready, setReady] = React.useState(Boolean(error));
  const [isRedirecting, setRedirecting] = React.useState(false);

  React.useEffect(() => {
    if (error) {
      return undefined;
    }
    const timer = setTimeout(() => setReady(true), 700);
    return () => clearTimeout(timer);
  }, [error]);

  const handleSignIn = () => {
    setRedirecting(true);
    userDataStore.login();
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {ready ? (
            <React.Fragment>
              <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
                <LockOutlinedIcon />
              </Avatar>
              <Typography component="h1" variant="h5">
                Sign in
              </Typography>
              <Box sx={{ mt: 3, width: "100%" }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  disabled={isRedirecting}
                  onClick={handleSignIn}
                >
                  {isRedirecting ? "Redirecting…" : "Sign in with Microsoft"}
                </Button>
              </Box>
            </React.Fragment>
          ) : (
            <CircularProgress />
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
});

export default Login;
